import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api';
import { Box } from '@mui/material';

import config from '@occc/config';
import { RetailerNetworkLocationFragment } from '@occc/graphql';

import { getStoreMapPinIcon } from './store-map-pin.icon';
import { useStoreMapPins } from './use-store-map-pins.hook';

const API_LOADER_CONFIG = {
  id: 'google-map-script',
  googleMapsApiKey: config.google.mapsApiKey || '',
};

const MAP_HEIGHT = 300;
const FALLBACK_CENTER = { lat: 31, lng: -97 };
const SINGLE_PIN_FOCUS_ZOOM = 14;

type StoreMapSectionProps = {
  boundsLocations: RetailerNetworkLocationFragment[];
  pinLocations: RetailerNetworkLocationFragment[];
  selectedLocationUuid: string | null;
  onPinClick: (locationUuid: string) => void;
};

export const StoreMapSection = ({
  boundsLocations,
  pinLocations,
  selectedLocationUuid,
  onPinClick,
}: StoreMapSectionProps) => {
  const { isLoaded } = useJsApiLoader(API_LOADER_CONFIG);

  const { pins, initialLoadComplete, onMapLoad } = useStoreMapPins({
    isLoaded,
    boundsLocations,
    pinLocations,
    singlePinZoom: SINGLE_PIN_FOCUS_ZOOM,
  });

  if (!isLoaded || !initialLoadComplete) return null;

  return (
    <Box
      sx={{
        borderRadius: '8px',
        overflow: 'hidden',
        boxShadow: '0px 2px 4px 0px rgba(0,0,0,0.25)',
        bgcolor: 'background.paper',
        width: '100%',
        position: 'relative',
      }}
    >
      <GoogleMap
        mapContainerStyle={{ width: '100%', height: MAP_HEIGHT }}
        center={FALLBACK_CENTER}
        zoom={4}
        onLoad={onMapLoad}
        options={{
          disableDefaultUI: true,
          zoomControl: true,
          gestureHandling: 'cooperative',
          clickableIcons: false,
        }}
      >
        {pins.map((pin) => (
          <Marker
            key={pin.uuid}
            position={{ lat: pin.lat, lng: pin.lng }}
            title={pin.name ?? undefined}
            onClick={() => onPinClick(pin.uuid)}
            options={{
              icon: getStoreMapPinIcon(selectedLocationUuid === pin.uuid),
            }}
          />
        ))}
      </GoogleMap>
    </Box>
  );
};
